import { useState } from 'react';
import {
    Box,
    Card,
    Grid,
    Stack,
    Switch,
    TextField,
    Typography,
    FormControlLabel,
    Button,
    MenuItem,
    alpha,
    useTheme,
    FormControl,
    InputAdornment,
    InputLabel,
    Select
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { paths } from 'src/routes/paths';
import { useCreateBannerMutation, useGetBannersQuery, useUpdateBannerMutation, usePutBannerTranslationsMutation } from 'src/context/api/onboard/api';
import { Banner, Content, Cta } from 'src/context/api/onboard/models';
import { bgGradient } from 'src/theme/css';
import MotivationIllustration from 'src/assets/illustrations/motivation-illustration';
import ReactMarkdown from 'react-markdown';
import { dashboardPaths } from 'src/routes/dashboardPaths';
import { Controller } from 'react-hook-form';
import { useRouter } from 'src/routes/hooks';
import { enqueueSnackbar } from 'notistack';

type BannerFormValues = Omit<Banner, 'id'>;

type BannerFormProps = Partial<Banner> & {
    onClose?: () => void;
}

// Helper function to convert camelCase to Title Case
export const toTitleCase = (str: string) => {
    const result = str.replace(/([A-Z])/g, " $1");
    return result.charAt(0).toUpperCase() + result.slice(1);
};

export default function AddBannerForm({ onClose, ...bannerState }: BannerFormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createBanner] = useCreateBannerMutation();
    const [updateBanner] = useUpdateBannerMutation();
    const [putBannerTranslations] = usePutBannerTranslationsMutation();
    const theme = useTheme();
    const router = useRouter();
    const [lang, setLang] = useState<"en" | "fr">("en");
    const defaultValues = {
        priority: bannerState.priority ?? 1,
        rebirth: bannerState.rebirth ?? false,
        target: bannerState.target ?? '',
        translations: bannerState.translations ?? {
            en: { title: '', content: '', ctas: [] },
            fr: { title: '', content: '', ctas: [] }
        }
    };

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
        control
    } = useForm<BannerFormValues>({
        defaultValues: defaultValues
    });

    const { refetch } = useGetBannersQuery(void 0);

    // Watch form values for preview
    const formValues = watch();

    const addActionButton = () => {
        const newButtons = formValues.translations?.[lang]?.ctas || [];
        if (newButtons.length < 2) {
            setValue(`translations.${lang}.ctas`, [...newButtons, { label: '', url: '', type: 'primary', placement: 'right' }]);
        }
    };

    const removeActionButton = (index: number) => {
        const newButtons = formValues.translations?.[lang]?.ctas?.filter((_, i) => i !== index) || [];
        setValue(`translations.${lang}.ctas`, newButtons);
    };

    const onSubmit = async (data: BannerFormValues) => {
        setIsSubmitting(true);
        try {
            if (bannerState.id) {
                await updateBanner({ id: bannerState.id, ...data }).unwrap()
                .then(() => {
                    return Promise.all(Object.keys(data.translations ?? {}).map((lang) => {
                        return putBannerTranslations({ id: bannerState.id as number, translation: Object.assign({}, data.translations?.[lang], {locale: lang}) as Content });
                    }));
                })
                .then(() => {
                    enqueueSnackbar("Banner updated successfully", { variant: 'success' });
                    refetch();
                    onClose?.();
                });
            } else {
                await createBanner(data).unwrap()
                .then((banner) => {
                    return Promise.all(Object.keys(data.translations ?? {}).map((lang) => {
                        return putBannerTranslations({ id: banner.id, translation: Object.assign({}, data.translations?.[lang], {locale: lang}) as Content });
                    }));
                })
                .then(() => {
                    enqueueSnackbar("Banner saved successfully", { variant: 'success' });
                    refetch();
                    router.push(paths.admin.onboard.banners.list);
                });
            }
        } catch (error) {
            enqueueSnackbar("Failed to save banner", { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Grid container spacing={3}>

            <Grid item xs={12} md={12}>
                <Card sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3 }}>Preview</Typography>

                    <Box
                        sx={{
                            height: 364,
                            p: 2,
                            borderRadius: 1,
                            bgcolor: 'background.neutral',
                            ...bgGradient({
                                direction: '135deg',
                                color: `to right, ${theme.palette.grey[900]} 25%, ${alpha(theme.palette.primary.darker, 0.88)}`,
                                imgUrl: '/assets/background/background-6.webp'
                            }),
                        }}
                    >

                        <Stack direction="row" justifyContent={"space-between"} spacing={2}>
                            <Stack direction="column" p={5} spacing={2}>
                                <Typography variant="h6">{formValues.translations?.[lang]?.title || 'Banner Title'}</Typography>
                                <Typography variant="body2">
                                    <ReactMarkdown>
                                        {formValues.translations?.[lang]?.content || 'Banner content will appear here'}
                                    </ReactMarkdown>
                                </Typography>
                            </Stack>

                            {formValues.image_url && (
                                <Box
                                    component="img"
                                    src={formValues.image_url}
                                    alt="Banner"
                                    alignSelf={formValues.image_placement === 'center' ? 'center' : formValues.image_placement === 'left' ? 'flex-start' : 'flex-end'}
                                    sx={{
                                        borderRadius: 1,
                                        borderTopLeftRadius: formValues.image_placement === 'left' ? 5 : 0,
                                        borderTopRightRadius: formValues.image_placement === 'right' ? 5 : 0,
                                        borderBottomLeftRadius: formValues.image_placement === 'left' ? 5 : 0,
                                        borderBottomRightRadius: formValues.image_placement === 'right' ? 5 : 0,
                                    }}
                                />
                            )}

                            {
                                !formValues.image_url && (
                                    <Box sx={{ display: 'flex', height: '100%' }}>
                                        <MotivationIllustration />
                                    </Box>
                                )
                            }
                        </Stack>

                        <Stack direction="row" p={5} spacing={2}>
                            {formValues.translations?.[lang]?.ctas?.map((cta: any, index: any) => (
                                cta.label && (
                                    <Button
                                        key={index}
                                        variant={cta.type === 'primary' ? 'contained' : 'outlined'}
                                        size="small"
                                        href={cta.url || '#'}
                                        target="_blank"
                                    >
                                        {cta.label}
                                    </Button>
                                )
                            ))}
                        </Stack>
                    </Box>

                    <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
                        * This is a preview of how the banner will appear
                    </Typography>
                </Card>
            </Grid>
            <Grid item xs={12} md={12}>
                <Card sx={{ p: 3 }}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing={3}>
                            <Controller
                                name="target"
                                control={control}
                                rules={{ required: 'Target URL is required' }}
                                render={({ field, fieldState: { error } }) => (
                                    <FormControl fullWidth error={!!error}>
                                        <InputLabel id="target-url-select-label">Target URL</InputLabel>
                                        <Select
                                            labelId="target-url-select-label"
                                            label="Target URL"
                                            {...field}
                                        >
                                            {Object.entries(dashboardPaths).map(([key, path]) => (
                                                <MenuItem key={key} value={path}>
                                                    {toTitleCase(key)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                                    </FormControl>
                                )}
                            />
                            <TextField
                                select
                                fullWidth
                                label="Priority"
                                {...register('priority', { required: 'Priority is required' })}
                                error={Boolean(errors.priority)}
                                helperText={errors.priority?.message}
                                defaultValue={defaultValues.priority}
                            >
                                <MenuItem value={1}>1 - Highest</MenuItem>
                                <MenuItem value={2}>2</MenuItem>
                                <MenuItem value={3}>3</MenuItem>
                                <MenuItem value={4}>4</MenuItem>
                                <MenuItem value={5}>5 - Lowest</MenuItem>
                            </TextField>

                            {bannerState.id &&
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...register('rebirth')}
                                            checked={formValues.rebirth}
                                            onChange={(e) => setValue('rebirth', e.target.checked)}
                                        />
                                    }
                                    label="Rebirth"
                                />
                            }

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>Translations</Typography>
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        variant={lang === 'en' ? 'contained' : 'outlined'}
                                        onClick={() => setLang('en')}
                                    >
                                        English
                                    </Button>
                                    <Button
                                        variant={lang === 'fr' ? 'contained' : 'outlined'}
                                        onClick={() => setLang('fr')}
                                    >
                                        French
                                    </Button>
                                </Stack>
                            </Box>
                            
                            <TextField
                                fullWidth
                                key={`title-${lang}`}
                                label={`Title (${lang.toUpperCase()})`}
                                {...register(`translations.${lang}.title`, { required: 'Title is required' })}
                                error={Boolean(errors.translations?.[lang]?.title)}
                                helperText={errors.translations?.[lang]?.title?.message}
                            />

                            <TextField
                                fullWidth
                                key={`content-${lang}`}
                                multiline
                                rows={4}
                                label={`Content (${lang.toUpperCase()})`}
                                {...register(`translations.${lang}.content`, { required: 'Content is required' })}
                                error={Boolean(errors.translations?.[lang]?.content)}
                                helperText={errors.translations?.[lang]?.content?.message}
                            />

                            <Typography variant="subtitle1">Action Buttons</Typography>
                            {formValues.translations?.[lang]?.ctas?.map((_, index) => (
                                <Stack key={`cta-${index}-${lang}`} direction="row" spacing={2} alignItems="center">
                                    <TextField
                                        key={`cta-label-${index}-${lang}`}
                                        fullWidth
                                        label={`Button ${index + 1} Label`}
                                        {...register(`translations.${lang}.ctas.${index}.label` as const)}
                                    />
                                    <TextField
                                        key={`cta-url-${index}-${lang}`}
                                        fullWidth
                                        label={`Button ${index + 1} URL`}
                                        {...register(`translations.${lang}.ctas.${index}.url` as const)}
                                    />
                                    <TextField
                                        key={`cta-type-${index}-${lang}`}
                                        select
                                        fullWidth
                                        label="Button Type"
                                        {...register(`translations.${lang}.ctas.${index}.type` as const)}
                                    >
                                        <MenuItem value="primary">Primary</MenuItem>
                                        <MenuItem value="secondary">Secondary</MenuItem>
                                    </TextField>
                                    <Button
                                        key={`cta-remove-${index}-${lang}`}
                                        onClick={() => removeActionButton(index)}
                                        color="error"
                                        size="small"
                                    >
                                        Remove
                                    </Button>
                                </Stack>
                            ))}

                            {(formValues.translations?.[lang]?.ctas || []).length < 2 && (
                                <Button
                                    variant="outlined"
                                    onClick={addActionButton}
                                    sx={{ alignSelf: 'flex-start' }}
                                >
                                    Add Action Button
                                </Button>
                            )}

                            <LoadingButton
                                fullWidth
                                type="submit"
                                variant="contained"
                                size="large"
                                loading={isSubmitting}
                            >
                                {bannerState.id ? 'Update Banner' : 'Create Banner'}
                            </LoadingButton>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => {
                                    if (bannerState.id) {
                                        onClose?.();
                                    } else {
                                        router.push(paths.admin.onboard.banners.list);
                                    }
                                }}
                            >
                                Cancel
                            </Button>

                        </Stack>
                    </form>
                </Card>
            </Grid>
        </Grid>
    );
}
